import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { CATEGORIES, getComicsByCategory } from '@/mocks/comics';

type SortOption = 'latest' | 'rating' | 'views';

export default function Category() {
  const { category = 'all' } = useParams<{ category: string }>();
  const [sortBy, setSortBy] = useState<SortOption>('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const currentCategory = CATEGORIES.find((c) => c.key === category) || CATEGORIES[0];
  const filteredComics = useMemo(() => getComicsByCategory(category), [category]);

  const sortedComics = useMemo(() => {
    const sorted = [...filteredComics];
    switch (sortBy) {
      case 'rating':
        return sorted.sort((a, b) => b.rating - a.rating);
      case 'views':
        return sorted.sort((a, b) => b.views - a.views);
      default:
        return sorted.sort(
          (a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()
        );
    }
  }, [filteredComics, sortBy]);

  const totalPages = Math.ceil(sortedComics.length / itemsPerPage);
  const paginatedComics = sortedComics.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleCategoryChange = (newCategory: string) => {
    setCurrentPage(1);
  };

  return (
    <>
      <Navbar />
      <main>
        {/* Page Header */}
        <section className="bg-background-100/70 py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '漫画分类', href: '/category/all' },
                ...(category !== 'all' ? [{ label: currentCategory.label }] : []),
              ]}
            />
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-4">
              {category === 'all' ? '全部漫画' : `${currentCategory.label}漫画`}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共 {filteredComics.length} 部
              {category !== 'all' ? ` ${currentCategory.label}类` : ''}
              漫画作品，布丁漫画免费在线韩国漫画阅读平台
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="bg-background-50 border-b border-background-200/70 px-4 md:px-6 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-1 overflow-x-auto py-2 flex-wrap">
              {CATEGORIES.map((cat) => (
                <Link
                  key={cat.key}
                  to={`/category/${cat.key}`}
                  onClick={() => handleCategoryChange(cat.key)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    category === cat.key
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Sort & Results */}
        <section className="bg-background-50 py-8 md:py-10 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                显示第 {(currentPage - 1) * itemsPerPage + 1}-
                {Math.min(currentPage * itemsPerPage, sortedComics.length)} 部，共{' '}
                {sortedComics.length} 部
              </p>
              <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
                {[
                  { key: 'latest' as const, label: '最新' },
                  { key: 'rating' as const, label: '评分' },
                  { key: 'views' as const, label: '人气' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => {
                      setSortBy(opt.key);
                      setCurrentPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      sortBy === opt.key
                        ? 'bg-background-50 text-foreground-950'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {paginatedComics.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                  {paginatedComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} size="medium" />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-1 mt-10">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-sm text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                          currentPage === page
                            ? 'bg-primary-500 text-background-50'
                            : 'text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-sm text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center bg-background-100 rounded-2xl mb-4">
                  <i className="ri-emotion-sad-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画</p>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Schema.org Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${currentCategory.label} - 布丁漫画韩国漫画分类`,
            description: `布丁漫画${currentCategory.label}类韩国漫画大全，免费在线阅读，官方源更新`,
            url: `https://budingmanhua.com/category/${category}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '布丁漫画',
              url: 'https://budingmanhua.com',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://budingmanhua.com' },
              {
                '@type': 'ListItem',
                position: 2,
                name: '漫画分类',
                item: 'https://budingmanhua.com/category/all',
              },
              ...(category !== 'all'
                ? [{ '@type': 'ListItem', position: 3, name: currentCategory.label }]
                : []),
            ],
          }),
        }}
      />

      <Footer />
    </>
  );
}